<template>
  <div class="column content-box">
    <woot-modal-header :header-title="pageTitle" />
    <form class="row" @submit.prevent="editCampaign">
      <div class="medium-12 columns">
        <woot-input
          v-model="title"
          :label="$t('CAMPAIGN.ADD.FORM.TITLE.LABEL')"
          type="text"
          :class="{ error: $v.title.$error }"
          :error="$v.title.$error ? $t('CAMPAIGN.ADD.FORM.TITLE.ERROR') : ''"
          :placeholder="$t('CAMPAIGN.ADD.FORM.TITLE.PLACEHOLDER')"
          @blur="$v.title.$touch"
        />

        <label class="editor-wrap">
          {{ $t('CAMPAIGN.ADD.FORM.MESSAGE.LABEL') }}
          <resizable-text-area
            v-model="message"
            class="message-editor"
            :class="{ editor_warning: $v.message.$error }"
            spellcheck="true"
            :placeholder="$t('CAMPAIGN.ADD.FORM.MESSAGE.PLACEHOLDER')"
            :min-height="4"
            @input="$v.message.$touch"
          />
          <span v-if="$v.message.$error" class="editor-warning__message">
            {{ $t('CAMPAIGN.ADD.FORM.MESSAGE.ERROR') }}
          </span>
        </label>

        <label :class="{ error: $v.selectedInbox.$error }">
          {{ $t('CAMPAIGN.ADD.FORM.INBOX.LABEL') }}
          <select v-model="selectedInbox" >
            <option v-for="item in inboxes" :key="item.name" :value="item.id">
              {{ item.name }}
            </option>
          </select>
          <span v-if="$v.selectedInbox.$error" class="message">
            {{ $t('CAMPAIGN.ADD.FORM.INBOX.ERROR') }}
          </span>
        </label>
        <label v-if = "isOneOffType || selectedType == 'one_off'">
          {{ $t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.LABEL') }}
          <woot-date-time-picker
            :value="scheduledAt"
            :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
            :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
            @change="onChange"
          />
        </label>
        <p v-if="showCampaignTypeInfo" class="campaign-info">
          <strong>Info:</strong>
          Message will be sent as <strong>{{ messageType }}</strong>. Estimated campaign cost:
          <strong>${{ estimatedCampaignCost }}</strong>.
        </p>

      </div>
      <div class="modal-footer">
        <woot-button :is-loading="uiFlags.isCreating">
          {{ $t('CAMPAIGN.EDIT.UPDATE_BUTTON_TEXT') }}
        </woot-button>
        <woot-button variant="clear" @click.prevent="onClose">
          {{ $t('CAMPAIGN.ADD.CANCEL_BUTTON_TEXT') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, url, minLength } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import campaignMixin from 'shared/mixins/campaignMixin';
import ResizableTextArea from '../../../../../shared/components/ResizableTextArea.vue';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';

export default {
  components: {
    WootDateTimePicker,
    ResizableTextArea,
  },
  mixins: [alertMixin, campaignMixin],
  props: {
    selectedCampaign: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      title: '',
      message: '',
      scheduledAt: '',
      selectedSender: '',
      selectedInbox: null,
      endPoint: '',
      timeOnPage: 10,
      triggerOnlyDuringBusinessHours: false,
      show: true,
      enabled: true,
      senderList: [],
      previousMessageType: 'SMS',
      showCampaignTypeInfo: false,
    };
  },
  validations: {
    title: {
      required,
    },
    message: {
      required,
    },
    selectedInbox: {
      required,
    },
    scheduledAt: {
      required,
    }
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaigns/getUIFlags',
      inboxes: 'inboxes/getTwilioInboxes',
      accountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    inboxes() {
      if (this.isOneOffType) {
        return this.$store.getters['inboxes/getSignalWireSMSInboxes'];
      }
    },
    pageTitle() {
      return `${this.$t('CAMPAIGN.EDIT.TITLE')} - ${this.selectedCampaign.title}`;
    },
    smsCost() {
      let account = this.getAccount(this.accountId);
      let cost = Number(account.sms_cost);
      return isNaN(cost) ? 0 : Math.floor(cost);
    },
    mmsCost() {
      let account = this.getAccount(this.accountId);
      let cost = Number(account.mms_cost);
      return isNaN(cost) ? 0 : Math.floor(cost);
    },
    messageType() {
      const containsUnicode = [...this.message].some(char => char.charCodeAt(0) > 127);
      const charLimit = containsUnicode ? 70 : 160;
      return this.message.length > charLimit ? 'MMS' : 'SMS';
    },
    estimatedCampaignCost() {
      const totalRecords = this.selectedCampaign?.max || 0;
      const costPerMessage = this.messageType === 'SMS' ? this.smsCost : this.mmsCost;
      return totalRecords * costPerMessage;
    }
  },
  mounted() {
    this.setFormValues();
  },
  watch: {
    message() {
      const currentType = this.messageType;
      this.messageTypeChanged(currentType);
    }
  },
  methods: {
    messageTypeChanged(currentType) {
      if (currentType !== this.previousMessageType) {
        this.showCampaignTypeInfo = true;
      } else {
        this.showCampaignTypeInfo = false;
      }
    },
    onClose() {
      this.$emit('on-close');
    },
    onChange(value){
      this.scheduledAt = value;
    },
    setFormValues() {
      const {
        title,
        message,
        inbox: { id: inboxId },
        scheduled_at
      } = this.selectedCampaign;

      this.title = title;
      this.message = message;
      this.scheduledAt = new Date(scheduled_at);
      this.selectedInbox = inboxId;
      this.previousMessageType = this.messageType; // store initial type
      this.showCampaignTypeInfo = false;
    },
    async editCampaign() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        await this.$store.dispatch('campaigns/update', {
          id: this.selectedCampaign.id,
          title: this.title,
          message: this.message,
          inbox_id: this.selectedInbox,
          scheduled_at: this.scheduledAt
        });
        this.showAlert(this.$t('CAMPAIGN.EDIT.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        this.showAlert(this.$t('CAMPAIGN.EDIT.API.ERROR_MESSAGE'));
      }
    },
  }
};
</script>
<style lang="scss" scoped>
::v-deep .ProseMirror-woot-style {
  height: 8rem;
}

.campaign-info {
  color: #0c5460;
  font-weight: bold;
  margin-top: 10px;
  padding: 10px;
  background-color: rgba(23, 162, 184, 0.1);
  border-radius: 4px;
}

</style>
